/**
 * Die Mail-Bühne der Werkstatt: die Mail in einem abgeschotteten Rahmen
 * (ohne Skripte) mit sechs Modi —
 *  ansehen       wie im Postfach, Links öffnen sich in einem neuen Tab
 *  kommentieren  Text markieren oder auf eine Stelle tippen → „Kommentieren"
 *  zeichnen      mit Maus, Finger oder Stift Stellen einkreisen
 *  bearbeiten    Text direkt ändern (Rohfassung, Platzhalter bleiben wörtlich);
 *                über der Auswahl eine Leiste wie in Word (fett, Farbe, Link, Emoji …)
 *  aenderungen   offene Vorschläge wie in Word: gestrichen / eingefügt
 *  entfernen     ganze Absätze, Kästen oder Zeilen samt Gestaltung zum Entfernen markieren
 * Die Bühne meldet, wo Kommentare und Vorschläge in der Mail stehen
 * (onPositionen), damit die Kommentarspalte ihre Karten daneben ausrichtet.
 *
 * Zeichnungen: x zählt ab der Mitte der Bühne, y ab der Oberkante des
 * Anker-Blocks (der Stelle unter der Zeichnung) — so bleibt sie bei anderer
 * Fensterbreite an ihrem Text. Ohne Anker zählt y ab dem oberen Rand; dann
 * wird sie nur bei ähnlicher Breite gezeichnet.
 */
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref } from 'react';
import { Check, Info, Maximize2, MessageSquarePlus, Minimize2, PenTool, Trash2, Undo2, X } from 'lucide-react';
import { vorschauHtml } from '../MailVorschau/vorschauHtml';
import {
  abschnittAenderung, abschnittBeschreibung, abschnittKontext, abschnittsKette,
  aenderungenEinblenden, aenderungenErmitteln, ankerFuer, ankerTextPasst, blockAusListe, blockZuKnoten,
  editierbarMachen, normalisieren, quellAbschnittPasst, quelltextLesen, textbloecke, zitatMarkieren, zitateEntfernen,
  type Abschnitt, type AbschnittGrund, type AbschnittKontext, type Aenderung, type BlockStand, type TextBlock,
} from '../../lib/zusammenarbeit';
import type { Quelltext } from '../../lib/zusammenarbeit';
import {
  auswahlImBlock, emojiEinfuegen, farbeSetzen, formatEntfernen, formatUmschalten, formatZustand, linkEntfernen, linkSetzen,
  type Farbe, type Format,
} from '../../lib/zusammenarbeit/formatieren';
import type { Anker, Markierung } from '../../lib/planung';
import { FormatLeiste, type LeistenMenue, type LeistenStand, type LinkErgebnis } from './FormatLeiste';
import {
  ENTWURF_ID, STIFT_FARBEN, aenderungLesbar, bewegungReduziert, stiftFarbe,
  type Breite, type BuehnenModus, type Entwurf,
} from './werkstattHilfen';

/** Kommentar (oder „Passt so") mit Bezug zur Mail. */
export interface BuehnenEintrag {
  id: string;
  anker: Anker | null;
  zitat: string | null;
  markierung: Markierung | null;
  /** Zitat und Zeichnung in der Mail zeigen (offene Einträge) */
  zeigen: boolean;
}

export interface BuehnenVorschlag {
  id: string;
  aenderungen: Aenderung[];
  /** offen → im Modus „Änderungen" eingeblendet */
  offen: boolean;
}

export interface AnkerWahl {
  anker: Anker;
  zitat: string | null;
  /** Höhe der Stelle ab Oberkante der Bühne (px) */
  y: number;
}

export interface BuehnenHandle {
  /** Bearbeiten: die Änderungen gegenüber dem Stand beim Öffnen */
  aenderungen(): Aenderung[];
  /** Bearbeiten: alles zurück auf den gespeicherten Stand */
  zuruecksetzen(): void;
  /** Entfernen: die markierten Abschnitte als Änderungen */
  abschnitte(): Aenderung[];
  /** Entfernen: alle Markierungen aufheben */
  abschnitteLeeren(): void;
  /** Zur Stelle eines Kommentars/Vorschlags scrollen */
  scrollZu(id: string): void;
}

type Props = {
  html: string;
  modus: BuehnenModus;
  breite: Breite;
  titel?: string;
  eintraege: BuehnenEintrag[];
  vorschlaege: BuehnenVorschlag[];
  /** Hervorgehobener Eintrag (Karte gewählt oder unter der Maus) */
  hervorgehoben: string | null;
  entwurf: Entwurf | null;
  /** Eintrag wählen; null = Auswahl aufheben (Klick auf eine leere Stelle) */
  onWaehlen: (id: string | null) => void;
  onAnker?: (w: AnkerWahl) => void;
  onMarkierung?: (m: Markierung, anker: Anker | null) => void;
  onZeichnenAbbrechen?: () => void;
  /** Höhe je id ab Oberkante der Bühne (px) */
  onPositionen?: (p: Map<string, number>) => void;
  /** Bearbeiten: Zahl geänderter Textblöcke */
  onBearbeitet?: (anzahl: number) => void;
  /** Zeichnen: Zahl der noch nicht gespeicherten Striche */
  onStriche?: (anzahl: number) => void;
  /** Entfernen: Zahl der markierten Abschnitte */
  onAbschnitte?: (anzahl: number) => void;
  /** Einträge, deren Zeichnung in dieser Ansicht nicht an ihre Stelle passt */
  onMarkierungOhneOrt?: (ids: ReadonlySet<string>) => void;
  onEscape?: () => void;
  ref?: Ref<BuehnenHandle>;
};

type Gruppe = 'vorschau' | 'roh' | 'aenderungen' | 'entfernen';
type Punkt = [number, number];

interface Angebot {
  anker: Anker;
  zitat: string | null;
  /** Koordinaten im Rahmen (px) */
  x: number;
  oben: number;
  unten: number;
  el: HTMLElement;
  /** Über der Auswahl läge der Knopf unter der klebenden Werkzeugleiste → darunter zeigen */
  drunter: boolean;
}

/** Ein markierter Abschnitt: die Stufen von klein nach groß, `stufe` ist die gewählte. */
interface Markiert { id: number; kette: Abschnitt[]; stufe: number }

/** Fürs Werkzeug: was der Nutzer sieht. */
interface MarkiertAnzeige {
  id: number;
  art: string;
  vorschau: string;
  beschreibung: string;
  aktiv: boolean;
  /** Beschreibung der nächsten Stufe (null = größer geht nicht) */
  groesser: string | null;
  kleiner: string | null;
}

/** Eine Fläche über der Mail (px im Rahmen): Abschnitt unter der Maus, markiert oder als Vorschlag. */
interface Flaeche {
  key: string;
  art: 'hover' | 'markiert' | 'vorschlag';
  x: number;
  y: number;
  b: number;
  h: number;
  label?: string;
  /** Entfernen: id der Markierung · Vorschlag: id des Eintrags */
  id?: number | string;
  aktiv?: boolean;
}

const GRUND_TEXT: Record<AbschnittGrund, string> = {
  leer: 'Hier gibt es nichts zu entfernen — tippe auf Text oder ein Bild.',
  abmelden: 'Dieser Teil enthält den Abmelde-Link und muss in der Mail bleiben.',
  alles: 'Das wäre fast die ganze Mail — wähle einen kleineren Abschnitt.',
  zu_gross: 'Dieser Abschnitt ist zu groß — wähle einen kleineren.',
  nicht_sauber: 'Diesen Abschnitt kann ich nicht sauber aus der Mail nehmen — wähle einen anderen.',
};

const MAX_STRICHE = 60;
const MAX_PUNKTE = 4000;
const MIN_ABSTAND = 2;
const ZITAT_MAX = 600;
const STIL_ID = 'za-buehne-stil';
/** Ohne Anker: so weit (px) darf die Breite der Bühne von der beim Zeichnen abweichen. */
const BREITE_SPIEL = 40;
/** Abstand des Kommentieren-Knopfs über der Auswahl (px). */
const ANGEBOT_ABSTAND = 46;
/** Abstand der Formatierungsleiste über der Auswahl (px). */
const LEISTE_ABSTAND = 50;
/** Tasten, nach denen die Leiste (wieder) erscheint: die Auswahl wurde bewegt, nicht getippt. */
const BEWEGUNG = new Set(['Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);
/** Eingaben, die im Bearbeiten nicht gehen: sie bauen Blöcke statt Text (Ausrichten, Einrücken). */
const BLOCK_FORMAT = /^format(Justify|Indent|Outdent)/;

/** Stil im Rahmen — feste Farben, er liegt auf der hellen Mail. */
const BUEHNE_STIL = `
html{overflow-y:hidden!important}
html[data-za-modus="kommentieren"] [data-za-b]{cursor:text;border-radius:3px;transition:outline-color .12s ease,background-color .12s ease}
html[data-za-modus="kommentieren"] [data-za-b]:hover{outline:1px dashed rgba(109,23,35,.35);outline-offset:2px}
html[data-za-modus="kommentieren"] a,html[data-za-modus="kommentieren"] a *{cursor:text!important}
html[data-za-modus="aenderungen"] [data-za-feedback] a{cursor:pointer!important}
html[data-za-modus="entfernen"] *{cursor:pointer!important}
[data-za-b].za-angebot{outline:2px solid rgba(109,23,35,.55)!important;outline-offset:2px;background-color:rgba(109,23,35,.05)!important}
[data-za-stelle="aktiv"]{outline:2px solid #6D1723!important;outline-offset:3px;border-radius:4px}
[data-za-stelle="entwurf"]{outline:2px solid #2563EB!important;outline-offset:3px;border-radius:4px;background-color:rgba(37,99,235,.06)!important}
mark.za-zitat[data-za-kommentar="${ENTWURF_ID}"]{background:#BFDBFE!important;box-shadow:inset 0 -2px 0 #2563EB!important}
@media (prefers-reduced-motion: reduce){*{transition:none!important}}
`;

function stilSetzen(doc: Document): void {
  if (doc.getElementById(STIL_ID)) return;
  const st = doc.createElement('style');
  st.id = STIL_ID;
  st.textContent = BUEHNE_STIL;
  (doc.head ?? doc.documentElement).appendChild(st);
}

/** Block eines Ankers: an seiner Stelle, sonst der einzige mit diesem Text, sonst die Stelle selbst. */
function ankerBlock(bloecke: TextBlock[], a: Anker): TextBlock | null {
  const amOrt = bloecke.find((b) => b.schluessel.tag === a.tag && b.schluessel.nr === a.nr);
  if (amOrt && ankerTextPasst(amOrt.text, a.text)) return amOrt;
  const gleich = bloecke.filter((b) => b.schluessel.tag === a.tag && ankerTextPasst(b.text, a.text));
  if (gleich.length === 1) return gleich[0];
  return amOrt ?? null;
}

/** Erster Block eines Vorschlags (vorher- oder schon übernommener Text). */
function vorschlagBlock(doc: Document, bloecke: TextBlock[], v: BuehnenVorschlag, roh: boolean): TextBlock | null {
  for (const a0 of v.aenderungen) {
    if (!a0?.block) continue;
    const a = roh ? a0 : aenderungLesbar(a0);
    const b = blockAusListe(doc, bloecke, a.block, a.vorher_text) ?? blockAusListe(doc, bloecke, a.block, a.nachher_text)
      ?? bloecke.find((x) => x.schluessel.tag === a.block.tag && x.schluessel.nr === a.block.nr) ?? null;
    if (b) return b;
  }
  return null;
}

/**
 * Wo Zeichnungen hingehören: je id der Versatz für y (Oberkante des
 * Anker-Blocks; ohne Anker 0). Fehlt der Block oder ist die Bühne ohne Anker
 * deutlich anders breit als beim Zeichnen, steht die id in `ohne`.
 */
function markierungsOrte(doc: Document, p: Props, weite: number): { orte: Map<string, number>; ohne: Set<string> } {
  const orte = new Map<string, number>();
  const ohne = new Set<string>();
  if (!doc.body) return { orte, ohne };
  let bloeckeCache: TextBlock[] | null = null;
  const bloecke = () => (bloeckeCache ??= textbloecke(doc));
  const sy = doc.defaultView?.scrollY ?? 0;
  const ort = (mk: Markierung, anker: Anker | null): number | null => {
    if (anker) {
      const b = ankerBlock(bloecke(), anker);
      return b ? b.el.getBoundingClientRect().top + sy : null;
    }
    return Math.abs(weite - mk.breite) <= BREITE_SPIEL ? 0 : null;
  };
  const merken = (id: string, mk: Markierung | null, anker: Anker | null) => {
    if (!mk || mk.ansicht !== p.breite) return;
    const y = ort(mk, anker);
    if (y === null) ohne.add(id);
    else orte.set(id, y);
  };
  for (const e of p.eintraege) merken(e.id, e.markierung, e.anker);
  if (p.entwurf) merken(ENTWURF_ID, p.entwurf.markierung, p.entwurf.anker);
  return { orte, ohne };
}

function markierungOben(mk: Markierung | null, ort: number | undefined): number | null {
  if (!mk || ort === undefined) return null;
  let min = Infinity;
  for (const s of mk.striche) for (const p of s) if (p[1] < min) min = p[1];
  return Number.isFinite(min) ? min + ort : null;
}

/** Wo stehen Einträge, Vorschläge und der Entwurf im Rahmen? (px ab Oberkante des Rahmens) */
function stellenImDoc(doc: Document, p: Props, gruppe: Gruppe, orte: Map<string, number>): Map<string, number> {
  const out = new Map<string, number>();
  if (!doc.body) return out;
  let bloeckeCache: TextBlock[] | null = null;
  const bloecke = () => (bloeckeCache ??= textbloecke(doc));
  const sy = doc.defaultView?.scrollY ?? 0;
  const yVon = (el: Element) => el.getBoundingClientRect().top + sy;
  const eintragY = (id: string, anker: Anker | null, markierung: Markierung | null): number | null => {
    const oben = markierungOben(markierung, orte.get(id));
    if (oben !== null) return oben;
    const mark = doc.querySelector(`mark.za-zitat[data-za-kommentar="${CSS.escape(id)}"]`);
    if (mark) return yVon(mark);
    if (anker) {
      const b = ankerBlock(bloecke(), anker);
      if (b) return yVon(b.el);
    }
    return null;
  };
  for (const e of p.eintraege) {
    const y = eintragY(e.id, e.anker, e.markierung);
    if (y !== null) out.set(e.id, y);
  }
  for (const v of p.vorschlaege) {
    const el = doc.querySelector(`[data-za-feedback="${CSS.escape(v.id)}"]`) ?? vorschlagBlock(doc, bloecke(), v, gruppe === 'roh')?.el;
    if (el) out.set(v.id, yVon(el));
  }
  if (p.entwurf) {
    const y = eintragY(ENTWURF_ID, p.entwurf.anker, p.entwurf.markierung);
    if (y !== null) out.set(ENTWURF_ID, y);
  }
  return out;
}

/** Text eines Bereichs wie der Blocktext: Zeilenumbruch = Leerzeichen, gestrichene Stellen zählen nicht. */
function bereichText(r: Range): string {
  let t = '';
  const laufen = (n: Node) => {
    for (let k = n.firstChild; k; k = k.nextSibling) {
      if (k.nodeType === 3) t += (k as Text).data;
      else if (k.nodeType === 1) {
        const name = (k as Element).localName;
        if (name === 'br') t += ' ';
        else if (name !== 'style' && name !== 'script' && !(name === 'del' && (k as Element).classList.contains('za-weg'))) laufen(k);
      }
    }
  };
  laufen(r.cloneContents());
  return normalisieren(t);
}

function rund(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Glatter Pfad durch die Punkte (Mittelpunkte als Stützstellen). */
function pfad(punkte: Punkt[]): string {
  if (punkte.length === 0) return '';
  const [x0, y0] = punkte[0];
  if (punkte.length < 3) {
    const [x1, y1] = punkte[punkte.length - 1];
    return `M${rund(x0)} ${rund(y0)}L${rund(x1)} ${rund(y1)}`;
  }
  let d = `M${rund(x0)} ${rund(y0)}`;
  for (let i = 1; i < punkte.length - 1; i++) {
    const [x, y] = punkte[i];
    const [nx, ny] = punkte[i + 1];
    d += `Q${rund(x)} ${rund(y)} ${rund((x + nx) / 2)} ${rund((y + ny) / 2)}`;
  }
  const [xe, ye] = punkte[punkte.length - 1];
  return `${d}L${rund(xe)} ${rund(ye)}`;
}

/** Läge y (px in der Hülle) unter der klebenden Werkzeugleiste der Werkstatt? */
function unterLeiste(huelle: HTMLElement | null, y: number): boolean {
  const leiste = huelle?.closest('.za-werkstatt')?.querySelector('.za-leiste');
  if (!huelle || !leiste) return false;
  return huelle.getBoundingClientRect().top + y < leiste.getBoundingClientRect().bottom + 6;
}

function zielElement(t: EventTarget | null): Element | null {
  const n = t as Node | null;
  if (!n) return null;
  return n.nodeType === 1 ? (n as Element) : n.parentElement;
}

export function MailBuehne(props: Props) {
  const { html, modus, breite, titel = 'Mail', eintraege, vorschlaege, hervorgehoben, entwurf, ref } = props;

  const rootRef = useRef<HTMLDivElement>(null);
  const huelleRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sprungRef = useRef<HTMLDivElement>(null);
  const aktivPfadRef = useRef<SVGPathElement>(null);
  const docRef = useRef<Document | null>(null);
  const gruppeRef = useRef<Gruppe>('vorschau');
  const staendeRef = useRef<BlockStand[]>([]);
  const aufraeumenRef = useRef<(() => void) | null>(null);
  const propsRef = useRef(props);
  const positionenSig = useRef('');
  const hoeheRef = useRef(600);
  const angebotRef = useRef<Angebot | null>(null);
  const strichRef = useRef<Punkt[] | null>(null);
  /** Finger/Stifte auf der Zeichenfläche; gezeichnet wird nur mit einem */
  const zeigerRef = useRef(new Set<number>());
  const strichZeigerRef = useRef<number | null>(null);
  const orteRef = useRef<Map<string, number>>(new Map());
  const orteSig = useRef('');
  const markiertRef = useRef<Markiert[]>([]);
  const aktivMarkRef = useRef<number | null>(null);
  const markNrRef = useRef(1);
  const kontextRef = useRef<AbschnittKontext | null>(null);
  const quellRef = useRef<Quelltext | null>(null);
  const hoverRef = useRef<{ ziel: Element; abschnitt: Abschnitt | null } | null>(null);
  const flaechenSig = useRef('');
  const flaechenFrame = useRef(0);
  /** Bearbeiten: zuletzt gültige Auswahl im Rahmen (fürs Link-Feld, das ihm den Fokus nimmt) */
  const auswahlRef = useRef<Range | null>(null);
  /** Bearbeiten: es wird getippt — die Leiste wartet, bis die Auswahl bewegt wird */
  const tipptRef = useRef(false);
  /** Bearbeiten: ein Befehl aus der Leiste läuft (dessen Eingabe-Ereignis ist kein Tippen) */
  const befehlRef = useRef(false);
  const leisteRef = useRef<LeistenStand | null>(null);
  const leistenMenueRef = useRef<LeistenMenue | null>(null);

  const [hoehe, setHoehe] = useState(600);
  const [weite, setWeite] = useState(0);
  const [ladeNr, setLadeNr] = useState(0);
  const [geladen, setGeladen] = useState(0);
  const [bereit, setBereit] = useState(false);
  const [angebot, setAngebotState] = useState<Angebot | null>(null);
  const [striche, setStriche] = useState<Punkt[][]>([]);
  const [farbe, setFarbe] = useState(STIFT_FARBEN[0].wert);
  const [grenze, setGrenze] = useState(false);
  const [orte, setOrte] = useState<Map<string, number>>(() => new Map());
  const [markiert, setMarkiert] = useState<MarkiertAnzeige[]>([]);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [flaechen, setFlaechen] = useState<Flaeche[]>([]);
  const [leiste, setLeisteState] = useState<LeistenStand | null>(null);
  const [leistenMenue, setLeistenMenueState] = useState<LeistenMenue | null>(null);

  useLayoutEffect(() => { propsRef.current = props; });

  const setAngebot = useCallback((a: Angebot | null) => {
    angebotRef.current = a;
    setAngebotState(a);
  }, []);

  const setLeistenMenue = useCallback((m: LeistenMenue | null) => {
    leistenMenueRef.current = m;
    setLeistenMenueState(m);
  }, []);

  const setLeiste = useCallback((l: LeistenStand | null) => {
    leisteRef.current = l;
    setLeisteState(l);
    if (!l) setLeistenMenue(null);
  }, [setLeistenMenue]);

  /** Bearbeiten: Auswahl merken und die Leiste darüber zeigen (nicht während getippt wird). */
  const leisteLesen = useCallback(() => {
    const doc = docRef.current;
    if (!doc || gruppeRef.current !== 'roh' || propsRef.current.modus !== 'bearbeiten') return;
    const z = formatZustand(doc);
    if (!z) {
      // Fokus in der Leiste (Link-Feld): Auswahl und Leiste bleiben
      if (doc.hasFocus()) { auswahlRef.current = null; setLeiste(null); }
      return;
    }
    const sel = doc.getSelection();
    auswahlRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    if (tipptRef.current) return;
    setLeiste({
      auswahl: z.auswahl,
      fett: z.fett,
      kursiv: z.kursiv,
      unterstrichen: z.unterstrichen,
      link: z.link,
      x: (z.links + z.rechts) / 2,
      oben: z.oben,
      unten: z.unten,
      drunter: unterLeiste(huelleRef.current, z.oben - LEISTE_ABSTAND),
    });
  }, [setLeiste]);

  const gruppe: Gruppe = modus === 'bearbeiten' ? 'roh' : modus === 'entfernen' ? 'entfernen' : modus === 'aenderungen' ? 'aenderungen' : 'vorschau';
  // Im Modus „Änderungen" neu laden, sobald sich offene Vorschläge ändern (Einblenden verändert das Dokument)
  const offenSig = useMemo(() => (gruppe !== 'aenderungen' ? '' : vorschlaege
    .filter((v) => v.offen)
    .map((v) => `${v.id}:${v.aenderungen.map((a) => a.ergebnis ?? 'o').join('')}`)
    .join('|')), [gruppe, vorschlaege]);
  const schluessel = `${gruppe}:${ladeNr}:${offenSig}`;
  // Bearbeiten und Entfernen arbeiten an der Rohfassung (Platzhalter wörtlich) — so passen Text und Nummern zum gespeicherten HTML
  const srcDoc = useMemo(() => (gruppe === 'roh' || gruppe === 'entfernen' ? html : vorschauHtml(html)), [gruppe, html]);
  const hatPlatzhalter = useMemo(() => /\{\{[^{}]+\}\}/.test(html), [html]);

  const eintragSig = useMemo(() => eintraege
    .map((e) => `${e.id}|${e.zeigen ? 1 : 0}|${e.zitat ?? ''}|${e.anker ? `${e.anker.tag}:${e.anker.nr}` : ''}|${e.markierung ? e.markierung.ansicht : ''}`)
    .join('\n'), [eintraege]);
  const vorschlagSig = useMemo(() => vorschlaege.map((v) => `${v.id}:${v.offen ? 1 : 0}`).join('|'), [vorschlaege]);
  const entwurfSig = entwurf
    ? `${entwurf.zitat ?? ''}|${entwurf.anker ? `${entwurf.anker.tag}:${entwurf.anker.nr}` : ''}|${entwurf.markierung?.striche.length ?? ''}`
    : '';

  // ── Flächen über der Mail ────────────────────────────────────────────────

  /** Rechtecke für Abschnitte unter der Maus, markierte und vorgeschlagene (px im Rahmen). */
  const flaechenAktualisieren = useCallback(() => {
    const doc = docRef.current;
    if (!doc?.body) return;
    const g = gruppeRef.current;
    const sx = doc.defaultView?.scrollX ?? 0;
    const sy = doc.defaultView?.scrollY ?? 0;
    const mass = (el: Element) => {
      const r = el.getBoundingClientRect();
      return { x: r.left + sx, y: r.top + sy, b: r.width, h: r.height };
    };
    const liste: Flaeche[] = [];
    if (g === 'entfernen') {
      for (const m of markiertRef.current) {
        const a = m.kette[m.stufe];
        if (a.el.isConnected) liste.push({ key: `m${m.id}`, art: 'markiert', id: m.id, aktiv: m.id === aktivMarkRef.current, label: `${a.art} entfernen`, ...mass(a.el) });
      }
      const hover = hoverRef.current?.abschnitt;
      if (hover?.el.isConnected && !markiertRef.current.some((m) => m.kette[m.stufe].el === hover.el)) {
        liste.push({ key: 'hover', art: 'hover', label: hover.art, ...mass(hover.el) });
      }
    } else if (g === 'aenderungen') {
      doc.querySelectorAll('[data-za-abschnitt]').forEach((el) => {
        const id = el.getAttribute('data-za-feedback') ?? '';
        liste.push({ key: `v${id}:${el.getAttribute('data-za-index') ?? ''}`, art: 'vorschlag', id, label: 'Entfernen vorgeschlagen', ...mass(el) });
      });
    }
    const sig = liste.map((f) => `${f.key}|${f.aktiv ? 1 : 0}|${Math.round(f.x)},${Math.round(f.y)},${Math.round(f.b)},${Math.round(f.h)}`).join(';');
    if (sig === flaechenSig.current) return;
    flaechenSig.current = sig;
    setFlaechen(liste);
  }, []);

  const flaechenBald = useCallback(() => {
    if (flaechenFrame.current) return;
    flaechenFrame.current = window.requestAnimationFrame(() => {
      flaechenFrame.current = 0;
      flaechenAktualisieren();
    });
  }, [flaechenAktualisieren]);

  // ── Messen und Positionen ────────────────────────────────────────────────

  const positionenMelden = useCallback(() => {
    const p = propsRef.current;
    const doc = docRef.current;
    const huelle = huelleRef.current;
    const root = rootRef.current;
    if (!p.onPositionen || !doc?.body || !huelle || !root) return;
    const basis = huelle.getBoundingClientRect().top - root.getBoundingClientRect().top;
    const m = new Map<string, number>();
    for (const [id, y] of stellenImDoc(doc, p, gruppeRef.current, orteRef.current)) m.set(id, Math.round(basis + y));
    const sig = [...m].map(([k, v]) => `${k}=${v}`).join(',');
    if (sig === positionenSig.current) return;
    positionenSig.current = sig;
    p.onPositionen(m);
  }, []);

  const messen = useCallback(() => {
    const doc = docRef.current;
    if (!doc?.body) return;
    const h = Math.ceil(Math.max(doc.documentElement.getBoundingClientRect().height, doc.body.scrollHeight));
    if (h > 0 && Math.abs(h - hoeheRef.current) > 1) {
      hoeheRef.current = h;
      setHoehe(h);
    }
    // Zeichnungen an ihren Anker-Block hängen (verschiebt sich beim Umbrechen)
    const p = propsRef.current;
    const { orte: neu, ohne } = markierungsOrte(doc, p, huelleRef.current?.clientWidth ?? 0);
    orteRef.current = neu;
    const sig = `${[...neu].map(([k, v]) => `${k}=${Math.round(v)}`).join(',')}|${[...ohne].join(',')}`;
    if (sig !== orteSig.current) {
      orteSig.current = sig;
      setOrte(neu);
      p.onMarkierungOhneOrt?.(ohne);
    }
    positionenMelden();
    flaechenAktualisieren();
  }, [positionenMelden, flaechenAktualisieren]);

  // ── Abschnitte markieren (Modus „Entfernen“) ─────────────────────────────

  /** Stand der Markierungen für Werkzeug, Flächen und die Werkstatt. */
  const markierungenMelden = useCallback(() => {
    const liste = markiertRef.current;
    setMarkiert(liste.map((m) => {
      const a = m.kette[m.stufe];
      const gr = m.kette[m.stufe + 1];
      const kl = m.kette[m.stufe - 1];
      return {
        id: m.id,
        art: a.art,
        vorschau: a.vorschau,
        beschreibung: abschnittBeschreibung(a),
        aktiv: m.id === aktivMarkRef.current,
        groesser: gr ? abschnittBeschreibung(gr) : null,
        kleiner: kl ? abschnittBeschreibung(kl) : null,
      };
    }));
    propsRef.current.onAbschnitte?.(liste.length);
    flaechenAktualisieren();
  }, [flaechenAktualisieren]);

  /** Enthält ein markierter Abschnitt den anderen? Dann zählt nur der größere. */
  const ohneVerschachtelung = useCallback((behalten: Markiert) => {
    const el = behalten.kette[behalten.stufe].el;
    markiertRef.current = markiertRef.current.filter((m) => m === behalten || !el.contains(m.kette[m.stufe].el));
  }, []);

  const markieren = useCallback((ziel: Element) => {
    const doc = docRef.current;
    const ctx = kontextRef.current;
    if (!doc || !ctx) return;
    // Auf einen markierten Abschnitt getippt: nur auswählen (für Größer/Kleiner)
    const schon = markiertRef.current.find((m) => m.kette[m.stufe].el.contains(ziel));
    if (schon) {
      aktivMarkRef.current = schon.id;
      setHinweis(null);
      markierungenMelden();
      return;
    }
    const q = (quellRef.current ??= quelltextLesen(propsRef.current.html));
    const { kette, grund } = abschnittsKette(doc, ziel, ctx, (a) => quellAbschnittPasst(q, abschnittAenderung(a)));
    if (!kette.length) {
      setHinweis(GRUND_TEXT[grund ?? 'leer']);
      return;
    }
    const neu: Markiert = { id: markNrRef.current++, kette, stufe: 0 };
    markiertRef.current = [...markiertRef.current, neu];
    ohneVerschachtelung(neu);
    aktivMarkRef.current = neu.id;
    setHinweis(null);
    markierungenMelden();
  }, [markierungenMelden, ohneVerschachtelung]);

  const stufeWechseln = useCallback((richtung: 1 | -1) => {
    const m = markiertRef.current.find((x) => x.id === aktivMarkRef.current);
    if (!m || !m.kette[m.stufe + richtung]) return;
    m.stufe += richtung;
    if (richtung > 0) ohneVerschachtelung(m);
    setHinweis(null);
    markierungenMelden();
  }, [markierungenMelden, ohneVerschachtelung]);

  const markierungAufheben = useCallback((id: number) => {
    markiertRef.current = markiertRef.current.filter((m) => m.id !== id);
    if (aktivMarkRef.current === id) aktivMarkRef.current = markiertRef.current[markiertRef.current.length - 1]?.id ?? null;
    markierungenMelden();
  }, [markierungenMelden]);

  const markierungWaehlen = useCallback((id: number) => {
    aktivMarkRef.current = id;
    markierungenMelden();
  }, [markierungenMelden]);

  const alleAufheben = useCallback(() => {
    markiertRef.current = [];
    aktivMarkRef.current = null;
    hoverRef.current = null;
    setHinweis(null);
    markierungenMelden();
  }, [markierungenMelden]);

  /** Hervorhebungen im Rahmen: gewählter Eintrag, Stelle des Entwurfs. */
  const hervorheben = useCallback(() => {
    const doc = docRef.current;
    if (!doc?.body || gruppeRef.current === 'roh') return;
    const p = propsRef.current;
    doc.querySelectorAll('.za-aktiv').forEach((e) => e.classList.remove('za-aktiv'));
    doc.querySelectorAll('[data-za-stelle]').forEach((e) => e.removeAttribute('data-za-stelle'));
    let bloeckeCache: TextBlock[] | null = null;
    const bloecke = () => (bloeckeCache ??= textbloecke(doc));
    const id = p.hervorgehoben;
    if (id) {
      const sel = CSS.escape(id);
      const marks = doc.querySelectorAll(`mark.za-zitat[data-za-kommentar="${sel}"]`);
      marks.forEach((m) => m.classList.add('za-aktiv'));
      const geaendert = doc.querySelectorAll(`[data-za-feedback="${sel}"]`);
      geaendert.forEach((e) => e.classList.add('za-aktiv'));
      if (!marks.length && !geaendert.length) {
        const e = p.eintraege.find((x) => x.id === id);
        const v = p.vorschlaege.find((x) => x.id === id);
        let el: HTMLElement | null = null;
        // Zeichnung hier nicht zu sehen (andere Ansicht): stattdessen ihre Stelle zeigen
        if (e?.anker && !orteRef.current.has(e.id)) el = ankerBlock(bloecke(), e.anker)?.el ?? null;
        else if (v) el = vorschlagBlock(doc, bloecke(), v, false)?.el ?? null;
        el?.setAttribute('data-za-stelle', 'aktiv');
      }
    }
    const ew = p.entwurf;
    if (ew?.anker && !ew.zitat && !ew.markierung) ankerBlock(bloecke(), ew.anker)?.el.setAttribute('data-za-stelle', 'entwurf');
  }, []);

  // ── Formatieren (Modus „Text ändern“) ────────────────────────────────────

  /** Befehl aus der Leiste im Rahmen ausführen, danach zählen, messen, Leiste auffrischen. */
  const formatieren = useCallback(<T,>(tun: (doc: Document, gemerkt: Range | null) => T): T | null => {
    const doc = docRef.current;
    if (!doc || gruppeRef.current !== 'roh') return null;
    befehlRef.current = true;
    let ergebnis: T;
    try {
      // Hat der Rahmen den Fokus (Klick in die Leiste nimmt ihn nicht), gilt die
      // Auswahl von jetzt — die gemerkte nur, wenn das Link-Feld den Fokus hat
      const jetzt = doc.hasFocus() ? auswahlImBlock(doc) : null;
      ergebnis = tun(doc, jetzt ? jetzt.range.cloneRange() : auswahlRef.current);
    } finally {
      befehlRef.current = false;
    }
    tipptRef.current = false;
    propsRef.current.onBearbeitet?.(aenderungenErmitteln(staendeRef.current).length);
    messen();
    leisteLesen();
    return ergebnis;
  }, [messen, leisteLesen]);

  const formatUm = useCallback((f: Format) => {
    formatieren((doc, r) => formatUmschalten(doc, f, r));
  }, [formatieren]);

  const faerben = useCallback((f: Farbe) => {
    formatieren((doc, r) => farbeSetzen(doc, f, r));
    setLeistenMenue(null);
  }, [formatieren, setLeistenMenue]);

  const verlinken = useCallback((href: string): LinkErgebnis => formatieren((doc, r) => linkSetzen(doc, href, r)) ?? 'fehler', [formatieren]);

  const linkAufheben = useCallback(() => {
    formatieren((doc, r) => linkEntfernen(doc, r));
    setLeistenMenue(null);
  }, [formatieren, setLeistenMenue]);

  const emojiSetzen = useCallback((e: string) => {
    formatieren((doc, r) => emojiEinfuegen(doc, e, r));
    setLeistenMenue(null);
  }, [formatieren, setLeistenMenue]);

  const formatWeg = useCallback(() => {
    formatieren((doc, r) => formatEntfernen(doc, r));
  }, [formatieren]);

  // ── Laden des Rahmens ────────────────────────────────────────────────────

  const beimLaden = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc?.body) return;
    aufraeumenRef.current?.();
    docRef.current = doc;
    positionenSig.current = '';
    const g = (iframe.dataset.gruppe as Gruppe | undefined) ?? 'vorschau';
    gruppeRef.current = g;
    const p = propsRef.current;
    stilSetzen(doc);
    doc.documentElement.setAttribute('data-za-modus', p.modus);
    // Neue Mail im Rahmen: Markierungen gehören zum alten Stand
    markiertRef.current = [];
    aktivMarkRef.current = null;
    hoverRef.current = null;
    quellRef.current = null;
    kontextRef.current = g === 'entfernen' ? abschnittKontext(doc) : null;
    auswahlRef.current = null;
    tipptRef.current = false;
    setLeiste(null);
    setHinweis(null);
    setMarkiert([]);
    p.onAbschnitte?.(0);

    if (g === 'roh') {
      staendeRef.current = editierbarMachen(doc);
    } else {
      staendeRef.current = [];
      for (const b of textbloecke(doc)) b.el.setAttribute('data-za-b', '');
      if (g === 'aenderungen') {
        aenderungenEinblenden(doc, p.vorschlaege
          .filter((v) => v.offen)
          .map((v) => ({ id: v.id, aenderungen: v.aenderungen.map(aenderungLesbar) })));
      }
    }

    const timer: number[] = [];
    let auswahlTimer = 0;
    let eingabeTimer = 0;

    const auswahl = () => {
      if (propsRef.current.modus !== 'kommentieren') return;
      const sel = doc.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
      const bereich = sel.getRangeAt(0);
      const block = blockZuKnoten(doc, bereich.startContainer) ?? blockZuKnoten(doc, bereich.commonAncestorContainer);
      if (!block) return;
      const r = bereich.cloneRange();
      if (!block.el.contains(r.startContainer)) r.setStart(block.el, 0);
      if (!block.el.contains(r.endContainer)) r.setEnd(block.el, block.el.childNodes.length);
      const zitat = bereichText(r).slice(0, ZITAT_MAX).trim();
      if (!zitat) return;
      const rect = r.getBoundingClientRect();
      setAngebot({
        anker: ankerFuer(block), zitat, x: rect.left + rect.width / 2, oben: rect.top, unten: rect.bottom, el: block.el,
        drunter: unterLeiste(huelleRef.current, rect.top - ANGEBOT_ABSTAND),
      });
    };
    const auswahlBald = () => {
      window.clearTimeout(auswahlTimer);
      auswahlTimer = window.setTimeout(auswahl, 160);
    };
    const mausHoch = () => { window.setTimeout(auswahl, 0); };

    const klick = (ev: MouseEvent) => {
      const q = propsRef.current;
      const ziel = zielElement(ev.target);
      if (!ziel) return;
      const link = ziel.closest('a');
      if (g === 'roh') {
        if (link) ev.preventDefault();
        return;
      }
      if (g === 'entfernen') {
        ev.preventDefault();
        markieren(ziel);
        return;
      }
      const fb = ziel.closest('[data-za-feedback]');
      if (link && (q.modus === 'kommentieren' || q.modus === 'zeichnen' || (q.modus === 'aenderungen' && fb))) ev.preventDefault();
      const markId = ziel.closest('mark.za-zitat[data-za-kommentar]')?.getAttribute('data-za-kommentar');
      if (markId && markId !== ENTWURF_ID) {
        setAngebot(null);
        q.onWaehlen(markId);
        return;
      }
      if (fb && q.modus === 'aenderungen') {
        const id = fb.getAttribute('data-za-feedback');
        if (id) q.onWaehlen(id);
        return;
      }
      // Klick daneben hebt die Auswahl auf (außer auf der Stelle des gewählten Eintrags)
      if (q.hervorgehoben && !ziel.closest('[data-za-stelle="aktiv"]')) q.onWaehlen(null);
      if (q.modus !== 'kommentieren') return;
      const sel = doc.getSelection();
      if (sel && !sel.isCollapsed && sel.toString().trim()) return;
      const block = blockZuKnoten(doc, ziel);
      if (!block) { setAngebot(null); return; }
      setAngebot({
        anker: ankerFuer(block), zitat: null, x: ev.clientX, oben: ev.clientY, unten: ev.clientY, el: block.el,
        drunter: unterLeiste(huelleRef.current, ev.clientY - ANGEBOT_ABSTAND),
      });
    };

    const bewegung = (ev: MouseEvent) => {
      if (g !== 'entfernen' || propsRef.current.modus !== 'entfernen') return;
      const ziel = zielElement(ev.target);
      const ctx = kontextRef.current;
      if (!ziel || !ctx || hoverRef.current?.ziel === ziel) return;
      hoverRef.current = { ziel, abschnitt: abschnittsKette(doc, ziel, ctx).kette[0] ?? null };
      flaechenBald();
    };
    const mausWeg = () => {
      if (!hoverRef.current) return;
      hoverRef.current = null;
      flaechenBald();
    };

    const taste = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        if (angebotRef.current) setAngebot(null);
        else if (leistenMenueRef.current) setLeistenMenue(null);
        else if (leisteRef.current) { tipptRef.current = true; setLeiste(null); }
        else propsRef.current.onEscape?.();
        return;
      }
      if (g !== 'roh' || !(ev.ctrlKey || ev.metaKey) || ev.altKey || ev.shiftKey) return;
      // Strg+B/I/U wie die Knöpfe der Leiste (gleiches HTML), Strg+K öffnet den Link
      const f: Format | null = /^b$/i.test(ev.key) ? 'fett' : /^i$/i.test(ev.key) ? 'kursiv' : /^u$/i.test(ev.key) ? 'unterstrichen' : null;
      if (f) {
        ev.preventDefault();
        auswahlRef.current = null;
        formatUm(f);
      } else if (/^k$/i.test(ev.key)) {
        ev.preventDefault();
        tipptRef.current = false;
        leisteLesen();
        if (leisteRef.current && (leisteRef.current.auswahl || leisteRef.current.link)) setLeistenMenue('link');
      }
    };
    const vorEingabe = (ev: Event) => {
      const typ = (ev as InputEvent).inputType ?? '';
      if (g === 'roh' && BLOCK_FORMAT.test(typ)) ev.preventDefault();
    };
    let leisteTimer = 0;
    const leisteBald = () => {
      window.clearTimeout(leisteTimer);
      leisteTimer = window.setTimeout(leisteLesen, 120);
    };
    const zeigerHoch = () => {
      tipptRef.current = false;
      leisteBald();
    };
    const tasteHoch = (ev: KeyboardEvent) => {
      if (BEWEGUNG.has(ev.key) || ((ev.ctrlKey || ev.metaKey) && /^a$/i.test(ev.key))) tipptRef.current = false;
      leisteBald();
    };
    const eingabe = () => {
      // Tippen blendet die Leiste aus — sie kommt mit der nächsten Auswahl wieder
      if (!befehlRef.current) {
        tipptRef.current = true;
        if (leisteRef.current) setLeiste(null);
      }
      window.clearTimeout(eingabeTimer);
      eingabeTimer = window.setTimeout(() => {
        if (docRef.current !== doc) return;
        propsRef.current.onBearbeitet?.(aenderungenErmitteln(staendeRef.current).length);
        messen();
      }, 200);
    };
    const geladenEtwas = (ev: Event) => {
      if ((ev.target as Element | null)?.localName === 'img') timer.push(window.setTimeout(messen, 30));
    };

    doc.addEventListener('click', klick, true);
    doc.addEventListener('keydown', taste, true);
    if (g === 'entfernen') {
      doc.addEventListener('mousemove', bewegung);
      doc.documentElement.addEventListener('mouseleave', mausWeg);
    }
    doc.addEventListener('load', geladenEtwas, true);
    doc.addEventListener('error', geladenEtwas, true);
    if (g === 'roh') {
      doc.addEventListener('beforeinput', vorEingabe, true);
      doc.addEventListener('input', eingabe);
      doc.addEventListener('selectionchange', leisteBald);
      doc.addEventListener('mouseup', zeigerHoch);
      doc.addEventListener('touchend', zeigerHoch);
      doc.addEventListener('keyup', tasteHoch);
    } else {
      doc.addEventListener('mouseup', mausHoch);
      doc.addEventListener('keyup', auswahlBald);
      doc.addEventListener('touchend', auswahlBald);
      doc.addEventListener('selectionchange', auswahlBald);
    }
    const beobachter = typeof ResizeObserver === 'function' ? new ResizeObserver(() => messen()) : null;
    beobachter?.observe(doc.body);
    for (const ms of [120, 500, 1500]) timer.push(window.setTimeout(messen, ms));

    aufraeumenRef.current = () => {
      doc.removeEventListener('click', klick, true);
      doc.removeEventListener('keydown', taste, true);
      doc.removeEventListener('mousemove', bewegung);
      doc.documentElement.removeEventListener('mouseleave', mausWeg);
      doc.removeEventListener('load', geladenEtwas, true);
      doc.removeEventListener('error', geladenEtwas, true);
      doc.removeEventListener('beforeinput', vorEingabe, true);
      doc.removeEventListener('input', eingabe);
      doc.removeEventListener('selectionchange', leisteBald);
      doc.removeEventListener('mouseup', zeigerHoch);
      doc.removeEventListener('touchend', zeigerHoch);
      doc.removeEventListener('keyup', tasteHoch);
      doc.removeEventListener('mouseup', mausHoch);
      doc.removeEventListener('keyup', auswahlBald);
      doc.removeEventListener('touchend', auswahlBald);
      doc.removeEventListener('selectionchange', auswahlBald);
      beobachter?.disconnect();
      timer.forEach((t) => window.clearTimeout(t));
      window.clearTimeout(auswahlTimer);
      window.clearTimeout(eingabeTimer);
      window.clearTimeout(leisteTimer);
    };

    setAngebot(null);
    setBereit(true);
    setGeladen((n) => n + 1);
    if (g === 'roh') p.onBearbeitet?.(0);
    messen();
  }, [messen, setAngebot, markieren, flaechenBald, setLeiste, setLeistenMenue, leisteLesen, formatUm]);

  // Neuer Inhalt / neuer Rahmen: bis zum Laden „lädt" (vor dem load-Ereignis gesetzt)
  useLayoutEffect(() => { setBereit(false); }, [schluessel, srcDoc]);

  useEffect(() => () => {
    aufraeumenRef.current?.();
    window.cancelAnimationFrame(flaechenFrame.current);
  }, []);

  // Breite der Hülle (für die Zeichenfläche) und Nachmessen beim Umbrechen
  useEffect(() => {
    const huelle = huelleRef.current;
    if (!huelle || typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(() => {
      setWeite(huelle.clientWidth);
      messen();
    });
    ro.observe(huelle);
    setWeite(huelle.clientWidth);
    return () => ro.disconnect();
  }, [messen]);

  // Modus im Rahmen bekannt machen (Stil), Angebot und Auswahl zurücksetzen
  useEffect(() => {
    const doc = docRef.current;
    if (doc?.documentElement) doc.documentElement.setAttribute('data-za-modus', modus);
    setAngebot(null);
    if (modus !== 'bearbeiten') setLeiste(null);
    if (modus !== 'kommentieren') doc?.getSelection()?.removeAllRanges();
    if (modus !== 'zeichnen') {
      strichRef.current = null;
      strichZeigerRef.current = null;
      zeigerRef.current.clear();
      setStriche((s) => (s.length ? [] : s));
      setGrenze(false);
    }
    const t = window.requestAnimationFrame(() => positionenMelden());
    return () => window.cancelAnimationFrame(t);
  }, [modus, positionenMelden, setAngebot, setLeiste]);

  // Formatierungsleiste: ein Klick außerhalb der Bühne blendet sie aus
  useEffect(() => {
    if (!leiste) return;
    const weg = (ev: PointerEvent) => {
      if (rootRef.current?.contains(ev.target as Node)) return;
      tipptRef.current = true;
      setLeiste(null);
    };
    document.addEventListener('pointerdown', weg, true);
    return () => document.removeEventListener('pointerdown', weg, true);
  }, [leiste, setLeiste]);

  // Zitate markieren (in place, ohne Neuladen) und Positionen melden
  useEffect(() => {
    const doc = docRef.current;
    if (!doc?.body) return;
    if (gruppeRef.current !== 'roh') {
      const p = propsRef.current;
      zitateEntfernen(doc);
      for (const e of p.eintraege) if (e.zeigen && e.zitat && e.anker) zitatMarkieren(doc, e.anker, e.zitat, e.id);
      if (p.entwurf?.zitat && p.entwurf.anker) zitatMarkieren(doc, p.entwurf.anker, p.entwurf.zitat, ENTWURF_ID);
    }
    positionenSig.current = '';
    messen();
    hervorheben();
  }, [geladen, eintragSig, vorschlagSig, entwurfSig, breite, hervorheben, messen]);

  useEffect(() => { hervorheben(); }, [hervorgehoben, geladen, hervorheben]);

  // Angebot: Block umranden
  useEffect(() => {
    const doc = docRef.current;
    if (!doc) return;
    doc.querySelectorAll('.za-angebot').forEach((e) => e.classList.remove('za-angebot'));
    angebot?.el.classList.add('za-angebot');
  }, [angebot]);

  useEffect(() => { propsRef.current.onStriche?.(striche.length); }, [striche.length]);

  useImperativeHandle(ref, () => ({
    aenderungen: () => (gruppeRef.current === 'roh' && docRef.current ? aenderungenErmitteln(staendeRef.current) : []),
    zuruecksetzen: () => {
      setLadeNr((n) => n + 1);
      propsRef.current.onBearbeitet?.(0);
    },
    abschnitte: () => (gruppeRef.current === 'entfernen' ? markiertRef.current.map((m) => abschnittAenderung(m.kette[m.stufe])) : []),
    abschnitteLeeren: () => alleAufheben(),
    scrollZu: (id: string) => {
      const doc = docRef.current;
      const ziel = sprungRef.current;
      if (!doc || !ziel) return;
      const y = stellenImDoc(doc, propsRef.current, gruppeRef.current, orteRef.current).get(id);
      if (y === undefined) return;
      ziel.style.top = `${Math.max(0, Math.round(y))}px`;
      ziel.scrollIntoView({ block: 'center', behavior: bewegungReduziert() ? 'auto' : 'smooth' });
    },
  }), [alleAufheben]);

  // ── Kommentieren ─────────────────────────────────────────────────────────

  const kommentieren = () => {
    const a = angebotRef.current;
    const huelle = huelleRef.current;
    const root = rootRef.current;
    if (!a || !huelle || !root) return;
    const basis = huelle.getBoundingClientRect().top - root.getBoundingClientRect().top;
    propsRef.current.onAnker?.({ anker: a.anker, zitat: a.zitat, y: Math.round(basis + a.oben) });
    docRef.current?.getSelection()?.removeAllRanges();
    setAngebot(null);
  };

  // ── Zeichnen ─────────────────────────────────────────────────────────────

  const punkteGesamt = useMemo(() => striche.reduce((n, s) => n + s.length, 0), [striche]);

  const punktBei = (svg: SVGSVGElement, clientX: number, clientY: number): Punkt => {
    const r = svg.getBoundingClientRect();
    return [
      rund(Math.min(Math.max(clientX - r.left, 0), r.width)),
      rund(Math.min(Math.max(clientY - r.top, 0), r.height)),
    ];
  };

  /** Laufenden Strich verwerfen (zweiter Finger = scrollen, oder der Browser bricht ab). */
  const strichVerwerfen = () => {
    strichRef.current = null;
    strichZeigerRef.current = null;
    aktivPfadRef.current?.setAttribute('d', '');
  };

  const strichBeginnen = (e: React.PointerEvent<SVGSVGElement>) => {
    if (modus !== 'zeichnen') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    zeigerRef.current.add(e.pointerId);
    // Mit zwei Fingern scrollt (oder zoomt) die Seite — touch-action: pinch-zoom
    if (zeigerRef.current.size > 1) { strichVerwerfen(); return; }
    if (striche.length >= MAX_STRICHE || punkteGesamt >= MAX_PUNKTE) { setGrenze(true); return; }
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    strichZeigerRef.current = e.pointerId;
    strichRef.current = [punktBei(e.currentTarget, e.clientX, e.clientY)];
    aktivPfadRef.current?.setAttribute('d', pfad(strichRef.current));
  };

  const strichWeiter = (e: React.PointerEvent<SVGSVGElement>) => {
    const s = strichRef.current;
    if (!s || e.pointerId !== strichZeigerRef.current) return;
    const roh = e.nativeEvent;
    const liste = typeof roh.getCoalescedEvents === 'function' ? roh.getCoalescedEvents() : [roh];
    for (const ev of liste.length ? liste : [roh]) {
      if (punkteGesamt + s.length >= MAX_PUNKTE) { setGrenze(true); break; }
      const pt = punktBei(e.currentTarget, ev.clientX, ev.clientY);
      const letzt = s[s.length - 1];
      if (Math.hypot(pt[0] - letzt[0], pt[1] - letzt[1]) < MIN_ABSTAND) continue;
      s.push(pt);
    }
    aktivPfadRef.current?.setAttribute('d', pfad(s));
  };

  const strichEnde = (e: React.PointerEvent<SVGSVGElement>) => {
    zeigerRef.current.delete(e.pointerId);
    if (e.pointerId !== strichZeigerRef.current) return;
    const s = strichRef.current;
    strichVerwerfen();
    if (s && s.length) setStriche((alt) => [...alt, s]);
  };

  const strichAbbruch = (e: React.PointerEvent<SVGSVGElement>) => {
    zeigerRef.current.delete(e.pointerId);
    if (e.pointerId === strichZeigerRef.current) strichVerwerfen();
  };

  const zeichnungFertig = () => {
    if (!striche.length) return;
    const huelle = huelleRef.current;
    const w = huelle?.clientWidth ?? weite;
    const mitte = w / 2;
    // Stelle unter der Zeichnung als Anker (für die Spalte, die andere Ansicht und
    // als Bezug: y zählt ab der Oberkante dieses Blocks)
    let anker: Anker | null = null;
    let basisY = 0;
    const doc = docRef.current;
    if (doc) {
      const alle = striche.flat();
      const xs = alle.map((p) => p[0]);
      const ys = alle.map((p) => p[1]);
      const kandidaten: Punkt[] = [
        [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2],
        striche[0][0],
      ];
      for (const [x, y] of kandidaten) {
        const el = doc.elementFromPoint(x, y);
        const block = el ? blockZuKnoten(doc, el) : null;
        if (block) {
          anker = ankerFuer(block);
          basisY = block.el.getBoundingClientRect().top + (doc.defaultView?.scrollY ?? 0);
          break;
        }
      }
    }
    const mk: Markierung = {
      ansicht: breite,
      breite: Math.round(w),
      hoehe: Math.round(hoeheRef.current),
      farbe,
      striche: striche.map((s) => s.map(([x, y]) => [Math.round(x - mitte), Math.round(y - basisY)] as [number, number])),
    };
    propsRef.current.onMarkierung?.(mk, anker);
    setStriche([]);
    setGrenze(false);
  };

  const zeichnungAbbrechen = () => {
    setStriche([]);
    setGrenze(false);
    propsRef.current.onZeichnenAbbrechen?.();
  };

  // ── Darstellung ──────────────────────────────────────────────────────────

  const mitte = weite / 2;
  const zeigeMarkierungen = modus === 'ansehen' || modus === 'kommentieren' || modus === 'zeichnen';
  const markierungen = zeigeMarkierungen
    ? eintraege.filter((e) => e.markierung && orte.has(e.id) && (e.zeigen || e.id === hervorgehoben))
    : [];
  const entwurfOrt = orte.get(ENTWURF_ID);
  const entwurfMarkierung = zeigeMarkierungen && entwurf?.markierung && entwurfOrt !== undefined ? entwurf.markierung : null;

  const angebotStil = angebot && weite
    ? {
      left: Math.min(Math.max(angebot.x, 76), Math.max(76, weite - 76)),
      top: angebot.oben > ANGEBOT_ABSTAND + 6 && !angebot.drunter ? angebot.oben - ANGEBOT_ABSTAND : angebot.unten + 10,
    }
    : undefined;

  return (
    <div ref={rootRef} className={`za-buehne za-buehne--${breite} za-buehne--${modus}`}>
      {modus === 'bearbeiten' && hatPlatzhalter && (
        <p className="za-platzhalter-hinweis">
          <Info size={14} aria-hidden="true" />
          <span>Text in <code>{'{{ … }}'}</code> sind Platzhalter, z. B. für den Vornamen — bitte so stehen lassen.</span>
        </p>
      )}
      <div ref={huelleRef} className={`za-huelle za-huelle--${breite}`}>
        <iframe
          key={schluessel}
          ref={iframeRef}
          data-gruppe={gruppe}
          title={titel}
          className="za-rahmen"
          // Ohne allow-scripts läuft im Rahmen nichts; allow-same-origin nur,
          // damit die Werkstatt messen, markieren und bearbeiten kann.
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          srcDoc={srcDoc}
          onLoad={beimLaden}
          style={{ height: hoehe }}
          tabIndex={modus === 'zeichnen' ? -1 : undefined}
        />
        {weite > 0 && (
          <svg
            className={`za-zeichenflaeche${modus === 'zeichnen' ? ' za-zeichenflaeche--aktiv' : ''}`}
            width={weite}
            height={hoehe}
            viewBox={`0 0 ${weite} ${hoehe}`}
            aria-hidden={modus === 'zeichnen' ? undefined : true}
            role={modus === 'zeichnen' ? 'img' : undefined}
            aria-label={modus === 'zeichnen' ? 'Zeichenfläche über der Mail' : undefined}
            onPointerDown={strichBeginnen}
            onPointerMove={strichWeiter}
            onPointerUp={strichEnde}
            onPointerCancel={strichAbbruch}
          >
            {markierungen.map((e) => {
              const mk = e.markierung!;
              const dy = orte.get(e.id) ?? 0;
              const d = mk.striche.map((s) => pfad(s.map(([x, y]) => [x + mitte, y + dy] as Punkt)));
              return (
                <g
                  key={e.id}
                  className={`za-mk${e.id === hervorgehoben ? ' za-mk--aktiv' : ''}`}
                  onClick={() => props.onWaehlen(e.id)}
                >
                  {d.map((x, i) => <path key={`t${i}`} d={x} className="za-mk-treffer" />)}
                  {d.map((x, i) => <path key={`s${i}`} d={x} className="za-mk-strich" stroke={stiftFarbe(mk.farbe)} />)}
                </g>
              );
            })}
            {entwurfMarkierung && (
              <g className="za-mk za-mk--entwurf">
                {entwurfMarkierung.striche.map((s, i) => (
                  <path key={i} d={pfad(s.map(([x, y]) => [x + mitte, y + (entwurfOrt ?? 0)] as Punkt))} className="za-mk-strich" stroke={stiftFarbe(entwurfMarkierung.farbe)} />
                ))}
              </g>
            )}
            {modus === 'zeichnen' && striche.map((s, i) => (
              <path key={`n${i}`} d={pfad(s)} className="za-mk-strich za-mk-strich--neu" stroke={farbe} />
            ))}
            {modus === 'zeichnen' && <path ref={aktivPfadRef} d="" className="za-mk-strich za-mk-strich--neu" stroke={farbe} />}
          </svg>
        )}
        {flaechen.length > 0 && weite > 0 && (
          <div className="za-flaechen" style={{ height: hoehe }}>
            {flaechen.map((f) => (
              <div
                key={f.key}
                className={`za-flaeche za-flaeche--${f.art}${f.aktiv ? ' za-flaeche--aktiv' : ''}${f.art === 'vorschlag' && f.id === hervorgehoben ? ' za-flaeche--hervor' : ''}`}
                style={{ left: f.x, top: f.y, width: f.b, height: f.h }}
              >
                {f.label && (f.art === 'markiert' ? (
                  <button
                    type="button"
                    className={`za-flaeche-label za-flaeche-label--knopf${f.y >= 24 ? ' za-flaeche-label--aussen' : ''}`}
                    onClick={() => markierungAufheben(f.id as number)}
                    aria-label={`${f.label}: Markierung aufheben`}
                    title="Markierung aufheben"
                  >
                    <Trash2 size={12} aria-hidden="true" /> {f.label} <X size={12} aria-hidden="true" />
                  </button>
                ) : (
                  <span className={`za-flaeche-label${f.y >= 24 ? ' za-flaeche-label--aussen' : ''}`}>{f.label}</span>
                ))}
              </div>
            ))}
          </div>
        )}
        {angebot && angebotStil && modus === 'kommentieren' && (
          <button
            type="button"
            className="za-angebot-knopf"
            style={angebotStil}
            onMouseDown={(e) => e.preventDefault()}
            onClick={kommentieren}
          >
            <MessageSquarePlus size={15} aria-hidden="true" />
            {angebot.zitat ? 'Kommentieren' : 'Zu dieser Stelle kommentieren'}
          </button>
        )}
        {modus === 'bearbeiten' && leiste && weite > 0 && (
          <FormatLeiste
            stand={leiste}
            weite={weite}
            menue={leistenMenue}
            onMenue={setLeistenMenue}
            onFormat={formatUm}
            onFarbe={faerben}
            onLink={verlinken}
            onLinkEntfernen={linkAufheben}
            onEmoji={emojiSetzen}
            onFormatEntfernen={formatWeg}
          />
        )}
        {modus === 'zeichnen' && striche.length === 0 && (
          <div className="za-zeichen-hinweis" aria-hidden="true">
            <PenTool size={14} /> Zeichne mit Maus, Finger oder Stift auf die Mail<span className="za-nur-touch"> · mit zwei Fingern scrollen</span>
          </div>
        )}
        {!bereit && (
          <div className="za-laedt" role="status">
            <span className="spinner" aria-hidden="true" />
            Mail wird geladen …
          </div>
        )}
        <div ref={sprungRef} className="za-sprungziel" aria-hidden="true" />
      </div>

      {modus === 'zeichnen' && (
        <div className="za-werkzeug" role="toolbar" aria-label="Zeichenstift">
          <div className="za-farben" role="radiogroup" aria-label="Farbe des Stifts">
            {STIFT_FARBEN.map((f) => (
              <button
                key={f.wert}
                type="button"
                role="radio"
                aria-checked={farbe === f.wert}
                aria-label={f.name}
                title={f.name}
                className={`za-farbe${farbe === f.wert ? ' za-farbe--an' : ''}`}
                style={{ '--za-farbe': f.wert } as React.CSSProperties}
                onClick={() => setFarbe(f.wert)}
              />
            ))}
          </div>
          <span className="za-werkzeug-trenner" aria-hidden="true" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setStriche((s) => s.slice(0, -1))}
            disabled={!striche.length}
            aria-label="Letzten Strich rückgängig machen"
            title="Letzten Strich rückgängig machen"
          >
            <Undo2 size={14} aria-hidden="true" /><span className="za-werkzeug-text">Rückgängig</span>
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={zeichnungAbbrechen}>Abbrechen</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={zeichnungFertig} disabled={!striche.length}>
            <Check size={14} aria-hidden="true" /> Fertig
          </button>
          {grenze && <span className="za-werkzeug-grenze" role="status">Mehr passt nicht in eine Markierung.</span>}
        </div>
      )}

      {modus === 'entfernen' && (
        <div className="za-werkzeug za-werkzeug--abschnitt" role="toolbar" aria-label="Abschnitt entfernen">
          {markiert.length === 0 ? (
            <span className={`za-werkzeug-info${hinweis ? ' za-werkzeug-info--warnung' : ''}`} role="status">
              <Trash2 size={14} aria-hidden="true" />
              {hinweis ?? 'Tippe in der Mail auf einen Absatz, einen Kasten oder eine Zeile.'}
            </span>
          ) : (
            <>
              <ul className="za-ab-liste" aria-label="Markierte Abschnitte">
                {markiert.map((m) => (
                  <li key={m.id} className={`za-ab-chip${m.aktiv ? ' za-ab-chip--an' : ''}`}>
                    <button
                      type="button"
                      className="za-ab-chip-text"
                      aria-pressed={m.aktiv}
                      onClick={() => markierungWaehlen(m.id)}
                      title={`${m.beschreibung}${m.vorschau ? `: ${m.vorschau}` : ''}`}
                    >
                      <strong>{m.art}</strong>{m.vorschau ? <span> „{m.vorschau}“</span> : <span> (Bild)</span>}
                    </button>
                    <button
                      type="button"
                      className="za-ab-chip-weg"
                      onClick={() => markierungAufheben(m.id)}
                      aria-label={`Markierung aufheben: ${m.art}`}
                      title="Markierung aufheben"
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
              {(() => {
                const aktiv = markiert.find((m) => m.aktiv);
                if (!aktiv) return null;
                return (
                  <>
                    <span className="za-werkzeug-trenner" aria-hidden="true" />
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={!aktiv.kleiner}
                      onClick={() => stufeWechseln(-1)}
                      title={aktiv.kleiner ? `Kleiner: ${aktiv.kleiner}` : 'Kleiner geht nicht'}
                    >
                      <Minimize2 size={14} aria-hidden="true" /><span className="za-werkzeug-text">Kleiner</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={!aktiv.groesser}
                      onClick={() => stufeWechseln(1)}
                      title={aktiv.groesser ? `Größer: ${aktiv.groesser}` : 'Größer geht nicht'}
                    >
                      <Maximize2 size={14} aria-hidden="true" /><span className="za-werkzeug-text">Größer</span>
                    </button>
                  </>
                );
              })()}
              {hinweis && <span className="za-werkzeug-info za-werkzeug-info--warnung" role="status">{hinweis}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
